import { compact } from 'lodash'
import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { TagProps } from '../components/app/design/Tag'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Contract } from './Contract'
import { Image } from './Image'
import { FKType } from './SQLBuilder'
import { Subaccount } from './Subaccount'
import { User } from './User'

type SignatureConstructorArgs = ConstructorArgs<() => Signature>

export class Signature extends BaseRecord {
    readonly recordType = 'signature'

    channel?: string
    signature?: Image
    signatureData?: string
    signedDate?: moment.Moment
    invoicedDate?: moment.Moment
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment
    readonly ipAddr?: string
    readonly viewedDate?: moment.Moment
    readonly sentDate?: moment.Moment

    constructor(data?: Partial<SignatureConstructorArgs>) {
        super(data)
        if (!data) {
            this.creator = PSApplication.me()
            this.employee = this.creator.employee
            this.owner = this.creator.owner
            return
        }

        this.channel = data.channel ?? undefined
        this.signature = this.propertyFromImageArg(data.signature, data.signature_uri)
        this.signatureData = data.signature_data ?? undefined
        this.signedDate = this.propertyFromTimestampArg(data.signed_date)
        this.invoicedDate = this.propertyFromTimestampArg(data.invoiced_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.ipAddr = data.ip_addr ?? undefined
        this.viewedDate = this.propertyFromTimestampArg(data.viewed_date)
        this.sentDate = this.propertyFromTimestampArg(data.sent_date)
    }

    clone(overrides?: Partial<SignatureConstructorArgs>): Signature {
        return new Signature({ ...this.serializeForClone(), ...overrides })
    }

    get expiredDate(): moment.Moment | undefined {
        const signedDate = this.signedDate
        if (signedDate === undefined) return undefined
        const expires = this.contract?.expires
        if (!expires) return undefined
        const field = expires.endsWith('m') ? 'months' : 'years'
        return moment(signedDate).add(
            parseInt(expires.replace('m', '').replace('y', '')),
            field as moment.unitOfTime.DurationConstructor
        )
    }

    get statusString(): string {
        if (this.signedDate && this.ipAddr)
            return `Signed ${this.signedDate.format('MMM d, YYYY')} (${this.ipAddr})`
        if (this.signedDate) return `Signed ${this.signedDate.format('MMM d, YYYY')}`
        if (this.cancelledDate) return `Canceled ${this.cancelledDate.format('MMM d, YYYY')}`
        if (this.id) return `Sent ${this.createdDate!.format('MMM d, YYYY')}`
        return 'Requires signature'
    }

    get statuses(): {
        title: string
        date: moment.Moment | undefined
        intent: TagProps['intent']
    }[] {
        return compact([
            {
                title: 'Sent',
                date: this.sentDate,
                intent: 'info',
            },
            {
                title: 'Viewed',
                date: this.viewedDate,
                intent: 'warning',
            },
            {
                title: 'Signed',
                date: this.signedDate,
                intent: 'success',
            },
            this.cancelledDate && {
                title: 'Canceled',
                date: this.cancelledDate,
                intent: 'danger',
            },
        ])
    }

    get statusTag(): { label: string; intent: TagProps['intent'] } | null {
        const lastActiveStatus = this.statuses.findLast((status) => status.date !== undefined)
        if (!lastActiveStatus) {
            return null
        }
        return { label: lastActiveStatus.title, intent: lastActiveStatus.intent }
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.employee) this.employee = BaseRecord.fromSerializer(data.employee, User)
        if (data.person) this.person = BaseRecord.fromSerializer(data.person, User)
        if (data.subaccount)
            this.subaccount = BaseRecord.fromSerializer(data.subaccount, Subaccount)
        if (data.contract) this.contract = BaseRecord.fromSerializer(data.contract, Contract)
        if (data.signature) this.signature = BaseRecord.fromSerializer(data.signature, Image)
        if (data.signedDate) this.signedDate = moment(data.signedDate)
        if (data.invoicedDate) this.invoicedDate = moment(data.invoicedDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'creator', 'employee', 'person'],
        subaccounts: ['subaccount'],
        contracts: ['contract'],
        images: ['signature'],
    }

    static dataType(): DataType {
        return 'signatures'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        if (!this.id && this.contract) dict['owner'] = this.contract.owner?.id
        dict['person'] = this.person?.id
        dict['employee'] = this.employee?.id ?? ''
        dict['contract'] = this.contract?.id
        if (this.channel) dict['channel'] = this.channel
        if (this.signatureData) dict['signature_data'] = this.signatureData
        dict['subaccount'] = this.subaccount?.id ?? ''
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get employee(): User | undefined {
        return this.getForeignRecord('employee', User)
    }
    set employee(employee: User | undefined) {
        this.setForeignRecord('employee', employee)
    }
    get subaccount(): Subaccount | undefined {
        return this.getForeignRecord('subaccount', Subaccount)
    }
    set subaccount(subaccount: Subaccount | undefined) {
        this.setForeignRecord('subaccount', subaccount)
    }
    get contract(): Contract | undefined {
        return this.getForeignRecord('contract', Contract)
    }
    set contract(contract: Contract | undefined) {
        this.setForeignRecord('contract', contract)
    }
}
